import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ArrowLeft, Lock, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AFFILIATE_LINKS } from '../constants/affiliates';
import { auth } from '../lib/firebase';

const questions = [
    { id: "A1", section: "A", category: "PERFORMANCE IDENTITY", text: "I feel most confident in relationships when I am being impressive, capable, or useful." },
    { id: "A2", section: "A", category: "PERFORMANCE IDENTITY", text: "I instinctively try to \"add value\" early when dating (advice, insight, emotional support)." },
    { id: "A3", section: "A", category: "PERFORMANCE IDENTITY", text: "I feel uncomfortable being desired for who I am, rather than what I do." },
    { id: "A4", section: "A", category: "PERFORMANCE IDENTITY", text: "I believe my independence and success are major reasons someone would choose me." },
    
    { id: "B1", section: "B", category: "EMOTIONAL EXPOSURE", text: "When I genuinely like someone, I become more self-aware and controlled." },
    { id: "B2", section: "B", category: "EMOTIONAL EXPOSURE", text: "I filter my thoughts or personality to avoid being perceived as \"too much.\"" },
    { id: "B3", section: "B", category: "EMOTIONAL EXPOSURE", text: "I feel a subtle pressure to maintain a certain image early in dating." },
    { id: "B4", section: "B", category: "EMOTIONAL EXPOSURE", text: "I worry that if I relax too much, attraction or respect will decrease." },
    
    { id: "C1", section: "C", category: "RELATIONSHIP DYNAMICS", text: "I feel more comfortable with partners who need guidance, support, or direction." },
    { id: "C2", section: "C", category: "RELATIONSHIP DYNAMICS", text: "I feel uneasy or uncertain around men who are fully stable and emotionally available." },
    { id: "C3", section: "C", category: "RELATIONSHIP DYNAMICS", text: "I tend to take on a \"leader,\" \"fixer,\" or \"manager\" role in relationships." },
    { id: "C4", section: "C", category: "RELATIONSHIP DYNAMICS", text: "I feel safer when I have more control than the other person." },
    
    { id: "D1", section: "D", category: "INTERNAL DIALOGUE", text: "If I’m not performing at a high level, I feel less valuable." },
    { id: "D2", section: "D", category: "INTERNAL DIALOGUE", text: "I subconsciously track my contributions to ensure I am 'staying in the black' emotionally." },
    { id: "D3", section: "D", category: "INTERNAL DIALOGUE", text: "Part of me believes that if someone truly saw everything about me, they might lose interest." },
    { id: "D4", section: "D", category: "INTERNAL DIALOGUE", text: "I often feel like I am \"too much\" and \"not enough\" at the same time." }
];

import { dbService } from '../services/dbService';

export default function WorthinessGapAudit() {
    const [step, setStep] = useState<'INTRO' | 'ASSESSMENT' | 'RESULTS'>('INTRO');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, number>>({});
    const [isCompiling, setIsCompiling] = useState(false);

    const handleStart = () => setStep('ASSESSMENT');

    const handleAnswer = (value: number) => {
        const question = questions[currentQuestionIndex];
        setAnswers(prev => ({ ...prev, [question.id]: value }));
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const calculateResults = () => {
        let A = 0, B = 0, C = 0, D = 0;
        
        questions.forEach((q, idx) => {
            const val = answers[q.id] || 3; // Default 3 for neutral
            if (idx < 4) A += val;
            else if (idx < 8) B += val;
            else if (idx < 12) C += val;
            else D += val;
        });

        const total = A + B + C + D;
        
        let gapType = "low";
        if (total >= 57) gapType = "high";
        else if (total >= 33) gapType = "moderate";

        let pattern = null;
        const Y = A + D;  // Performance & Internal
        const Se = B + C; // Exposure & Dynamics
        
        if (total >= 56 && Math.abs(Y - Se) <= 4) {
            pattern = "disqualified";
        } else if (Y > Se) {
            pattern = "performer";
        } else {
            pattern = "controller";
        }

        return { total, gapType, pattern };
    };

    const handleNext = async () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setStep('RESULTS');
            setIsCompiling(true);

            // Save to DB
            try {
                const user = auth.currentUser;
                if (!user) throw new Error("User not found");

                const { gapType, pattern } = calculateResults();
                const titleMap: Record<string, string> = {
                    "low": "The Discernment Trap",
                    "moderate": "Oscillation Detected",
                    "high": "Performance Identity"
                };
                const summaryMap: Record<string, string> = {
                    "performer": "Identity derived from deliverable value.",
                    "controller": "Safety maintained through systemic control.",
                    "disqualified": "Simultaneous volatility of 'too much' and 'not enough'."
                };

                await dbService.saveAuditResult(user.uid, {
                    auditType: 'worthiness_gap',
                    title: 'Worthiness Gap Audit',
                    diagnostic: titleMap[gapType],
                    summary: summaryMap[pattern || ''] || "Relational performance analyzed."
                });
            } catch (err) {
                console.error("Failed to persist audit results:", err);
            }

            setTimeout(() => setIsCompiling(false), 3000);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen flex flex-col font-sans text-theme-text bg-theme-bg selection:bg-gold/30">
            <main className="flex-grow flex flex-col items-center justify-start w-full pt-20 pb-24">
                <AnimatePresence mode="wait">
                    {step === 'INTRO' && (
                        <motion.div
                            key="intro"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="flex flex-col items-center text-center max-w-2xl mx-auto px-6 py-12"
                        >
                            <h1 className="font-serif text-4xl md:text-5xl uppercase tracking-[0.15em] text-theme-text mb-8 leading-tight">
                                The Worthiness Gap Audit
                            </h1>
                            <div className="w-16 h-[1px] bg-gold mb-8"></div>
                            <p className="text-theme-text/80 text-lg leading-relaxed mb-6 font-light">
                                A Private Diagnostic for High-Performing Women
                            </p>
                            <p className="text-theme-text/60 text-sm leading-relaxed mb-12 max-w-xl mx-auto italic">
                                This clinical-style self-assessment identifies whether you are operating from performance or inherent worth in your relationships. 
                                Answer each statement honestly based on your default patterns—who you are under emotional pressure.
                            </p>

                            <button
                                onClick={handleStart}
                                className="group relative px-8 py-4 bg-transparent border border-gold text-theme-text uppercase tracking-widest text-sm font-medium overflow-hidden transition-all hover:text-white cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out -z-10"></div>
                                <span className="flex items-center gap-3 relative z-10 group-hover:text-theme-bg transition-colors">
                                    Commence Audit <ArrowRight className="w-4 h-4" />
                                </span>
                            </button>
                        </motion.div>
                    )}

                    {step === 'ASSESSMENT' && (
                        <motion.div
                            key="assessment"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="w-full max-w-3xl mx-auto px-6 py-12 flex flex-col items-center pt-24"
                        >
                            <div className="w-full max-w-xl h-[1px] bg-theme-border flex-grow-0 mb-12 relative">
                                <motion.div
                                    className="absolute top-0 left-0 h-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.5)]"
                                    initial={{ width: 0 }}
                                    animate={{ width: ((currentQuestionIndex) / questions.length) * 100 + '%' }}
                                    transition={{ duration: 0.5 }}
                                />
                            </div>

                            <div className="text-center mb-16 flex flex-col items-center gap-2">
                                <span className="font-serif text-gold tracking-widest uppercase text-base md:text-lg font-semibold">
                                    SECTION {questions[currentQuestionIndex].section}: {questions[currentQuestionIndex].category}
                                </span>
                                <span className="text-xs tracking-widest text-taupe uppercase font-semibold">
                                    {currentQuestionIndex + 1} / {questions.length}
                                </span>
                            </div>

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentQuestionIndex}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="w-full flex flex-col items-center"
                                >
                                    <h2 className="text-2xl md:text-3xl font-light text-theme-text text-center leading-relaxed mb-16 min-h-[120px] flex items-center italic">
                                        "{questions[currentQuestionIndex].text}"
                                    </h2>

                                    <div className="w-full max-w-3xl mx-auto">
                                        <div className="flex flex-wrap justify-center sm:flex-nowrap sm:justify-between items-start gap-3 sm:gap-2 w-full">
                                            {[
                                                { val: 1, label: "Not true at all" },
                                                { val: 2, label: "Slightly true" },
                                                { val: 3, label: "Moderately true" },
                                                { val: 4, label: "Very true" },
                                                { val: 5, label: "Extremely true" }
                                            ].map(({ val, label }) => {
                                                const isSelected = answers[questions[currentQuestionIndex].id] === val;
                                                return (
                                                    <div key={val} className="flex flex-col items-center gap-3 w-1/4 sm:w-auto">
                                                        <button
                                                            onClick={() => handleAnswer(val)}
                                                            className={"relative w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center transition-all duration-300 cursor-pointer shrink-0 " + (isSelected ? "border-gold bg-gold text-theme-bg scale-110 shadow-[0_0_15px_rgba(212,175,55,0.4)]" : "border-theme-border text-theme-text hover:border-gold hover:text-gold hover:scale-110 hover:shadow-[0_0_15px_rgba(212,175,55,0.2)]")}
                                                        >
                                                            <span className="text-sm sm:text-base font-medium">{val}</span>
                                                        </button>
                                                        <span className={"text-[8px] sm:text-[10px] uppercase tracking-widest text-center leading-tight transition-colors mt-2 max-w-[80px] " + (isSelected ? "text-gold font-bold" : "text-taupe")}>
                                                            {label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="flex justify-between w-full mt-24 px-4 border-t border-theme-border pt-8">
                                            <button
                                                onClick={handlePrev}
                                                disabled={currentQuestionIndex === 0}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold " + (currentQuestionIndex === 0 ? "text-taupe/30 cursor-not-allowed" : "text-taupe hover:text-gold cursor-pointer")}
                                            >
                                                <ArrowLeft className="w-4 h-4" /> Previous
                                            </button>
                                            <button
                                                onClick={handleNext}
                                                disabled={!answers[questions[currentQuestionIndex].id]}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold px-6 py-3 border inline-flex " + (!answers[questions[currentQuestionIndex].id] ? "border-transparent text-taupe/30 cursor-not-allowed" : "border-gold text-gold hover:bg-gold hover:text-theme-bg cursor-pointer")}
                                            >
                                                {currentQuestionIndex === questions.length - 1 ? 'Complete Audit' : 'Next Step'} <ArrowRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                    )}

                    {step === 'RESULTS' && (() => {
                        if (isCompiling) {
                            return (
                                <motion.div
                                    key="compiling"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="w-full max-w-4xl mx-auto px-6 py-24 flex flex-col items-center justify-center min-h-[50vh]"
                                >
                                    <div className="relative w-24 h-24 mb-12">
                                        <div className="absolute inset-0 border-t-2 border-l-2 border-gold rounded-full animate-[spin_2s_linear_infinite]"></div>
                                        <div className="absolute inset-2 border-r-2 border-b-2 border-taupe rounded-full animate-[spin_3s_linear_infinite_reverse]"></div>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <ShieldCheck className="w-6 h-6 text-gold animate-pulse" />
                                        </div>
                                    </div>
                                    <h2 className="font-serif text-xl md:text-2xl uppercase tracking-[0.2em] text-gold mb-4 text-center">
                                        Calculating Systemic Risk Profile...
                                    </h2>
                                    <p className="text-theme-text/60 font-light text-sm tracking-widest animate-pulse text-center">
                                        Analyzing performance mechanisms...
                                    </p>
                                </motion.div>
                            );
                        }

                        const profile = calculateResults();
                        const percentage = (profile.total / 80) * 100;
                        
                        const containerVariants = {
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: { staggerChildren: 0.2, delayChildren: 0.1 }
                            }
                        };

                        const itemVariants = {
                            hidden: { opacity: 0, y: 20 },
                            visible: { 
                                opacity: 1, y: 0, 
                                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } as any
                            }
                        };

                        return (
                            <motion.div
                                key="results"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="w-full max-w-4xl mx-auto px-6 py-12 pb-24"
                            >
                                <motion.div variants={itemVariants} className="text-center mb-16">
                                    <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Clinical Report</span>
                                    <h1 className="font-serif text-4xl md:text-5xl uppercase tracking-widest text-theme-text mt-6 mb-6">
                                        {profile.gapType === "low" && "The Discernment Trap"}
                                        {profile.gapType === "moderate" && "Oscillation Detected"}
                                        {profile.gapType === "high" && "Performance Identity"}
                                    </h1>
                                </motion.div>

                                <motion.div variants={itemVariants} className="w-full h-[1px] bg-gold/30 mb-16" />

                                <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-12 md:gap-16 mb-16">
                                    <div>
                                        <h3 className="font-serif text-xl uppercase tracking-widest text-theme-text mb-6 flex items-center gap-3">
                                            <div className="w-2 h-2 bg-gold rotate-45"></div>
                                            Worthiness Gap Score
                                        </h3>
                                        <div className="bg-black/20 p-8 border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 shadow-md backdrop-blur-sm group">
                                            <div className="flex justify-between text-xs tracking-[0.2em] uppercase text-theme-text/80 font-bold mb-4">
                                                <span>Total Matrix Score</span>
                                                <span className="text-gold">{profile.total} / 80</span>
                                            </div>
                                            <div className="w-full h-1 bg-theme-border/30 relative overflow-hidden mb-6">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${percentage}%` }}
                                                    transition={{ duration: 1.5, ease: "easeOut" }}
                                                    className="absolute top-0 left-0 h-full bg-gold"
                                                />
                                            </div>

                                            {profile.gapType === "low" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed">
                                                    You do not heavily rely on performance to feel valued in relationships. You are able to be seen without overcompensating, allow connection without control, and maintain identity in intimacy.
                                                </p>
                                            )}
                                            {profile.gapType === "moderate" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed">
                                                    You have access to an authentic connection... But under pressure, you revert to control, performance, or emotional filtering. You oscillate between authenticity and control.
                                                </p>
                                            )}
                                            {profile.gapType === "high" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed">
                                                    You rely on competence, control, or value-adding behaviors to feel secure in relationships. Vulnerability feels risky, exposing, and drastically destabilizing.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="font-serif text-xl uppercase tracking-widest text-theme-text mb-6 flex items-center gap-3">
                                            <div className="w-2 h-2 bg-gold rotate-45"></div>
                                            Systemic Pattern Analysis
                                        </h3>
                                        <div className="bg-black/20 p-8 border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 shadow-md backdrop-blur-sm group">
                                            {profile.gapType === "low" ? (
                                                <>
                                                    <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold">Primary Risk</strong>
                                                    <span className="text-theme-text/80 font-light text-sm leading-relaxed block mb-4">
                                                        You tolerate misaligned partners for too long, over-estimating your capacity to carry the emotional load for two simply because their behavior doesn't immediately threaten your identity.
                                                    </span>
                                                </>
                                            ) : profile.gapType === "moderate" ? (
                                                <>
                                                    <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold">Pattern Identification</strong>
                                                    <span className="text-theme-text/80 font-light text-sm leading-relaxed block mb-4">
                                                        You don't struggle with worth. You struggle with trusting it consistently. Your growth lies in the consistency of emotional exposure and stopping the filter cycle.
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold">Pattern Identification</strong>
                                                    <span className="text-theme-text/80 font-light text-sm leading-relaxed block mb-4">
                                                        You are over-functioning in love... and fundamentally under-receiving.
                                                    </span>
                                                </>
                                            )}

                                            <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold mt-6">Dynamic Type</strong>
                                            
                                            {profile.pattern === "performer" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed mt-2">
                                                    <span className="text-white italic block mb-2">The Performer:</span>
                                                    You believe your value comes from what you deliver. 
                                                    <br/><br/>
                                                    <strong>Core Fear:</strong> "If I stop performing, I lose my value."
                                                </p>
                                            )}
                                            {profile.pattern === "controller" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed mt-2">
                                                    <span className="text-white italic block mb-2">The Controller:</span>
                                                    You maintain safety through control and perception management.
                                                    <br/><br/>
                                                    <strong>Core Fear:</strong> "If I'm not in control, I'm exposed."
                                                </p>
                                            )}
                                            {profile.pattern === "disqualified" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed mt-2">
                                                    <span className="text-white italic block mb-2">The Disqualified:</span>
                                                    You feel both "too much" and "not enough" simultaneously.
                                                    <br/><br/>
                                                    <strong>Core Fear:</strong> "I am fundamentally too much and not enough."
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={itemVariants} className="w-full h-[1px] bg-gold/30 mb-20" />

                                <motion.div variants={itemVariants} className="bg-black/20 p-8 md:p-12 text-center relative overflow-hidden shadow-2xl border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 backdrop-blur-sm group">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gold hover:opacity-100 transition-opacity"></div>
                                    <h3 className="font-serif text-xl md:text-2xl uppercase tracking-widest mb-6 text-theme-text">Final Clinical Insight</h3>
                                    
                                    <p className="text-theme-text/70 font-light text-sm md:text-base mb-8 max-w-2xl mx-auto leading-relaxed">
                                        This pattern did not begin in your dating life. It began as an adaptation that taught you how to be valued, but not how to feel inherently worthy. And until that distinction is addressed, the gap will persist regardless of who you meet.
                                    </p>
                                    <p className="text-theme-text/70 font-light text-sm md:text-base mb-10 max-w-2xl mx-auto leading-relaxed border-t border-theme-border pt-8">
                                        Awareness doesn't close this gap. Because this isn't just a mindset. It's a pattern your nervous system has been running for years. Structured approaches like cognitive behavioral therapy are designed to help you identify and retrain these responses in real time.
                                    </p>

                                    <div className="w-full flex flex-col items-center gap-6 mt-8">
                                        <a href={AFFILIATE_LINKS.ONLINE_THERAPY} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block w-full sm:w-auto px-12">
                                            Clinical Protocol: Cognitive Retraining
                                        </a>
                                        <a href={AFFILIATE_LINKS.BOOKS.DRAMA_OF_GIFTED_CHILD} target="_blank" rel="noopener noreferrer" className="text-[10px] tracking-widest uppercase text-theme-text/60 hover:text-gold transition-colors block pb-2 border-b border-transparent hover:border-gold/30">
                                            Foundational Study: The Drama of the Gifted Child by Alice Miller
                                        </a>
                                        <Link to="/vault" className="text-[10px] tracking-widest uppercase text-theme-text/60 hover:text-gold transition-colors block pb-2 border-b border-transparent hover:border-gold/30">
                                            VIP Vault Resources
                                        </Link>
                                    </div>
                                    <p className="text-[8px] text-theme-text/40 italic text-center mt-12 uppercase tracking-widest">
                                        *The resources above contain affiliate links. We receive a commission for every qualified purchase.
                                    </p>
                                </motion.div>
                            </motion.div>
                        );
                    })()}
                </AnimatePresence>
            </main>
        </div>
    );
}
